import uniqid from "uniqid"
import {
    useAddCommentToApplication,
    useAddVoteToApplication,
    useFetchApplication,
    useFetchTrustees,
    useUpdateApplicationCategory,
    useUpdateApplicationStatus,
    useArchiveApplications,
    useUnarchiveApplications,
} from "@/api/applicationQueries"
import { useGetUserById } from "@/api/userQueries"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogPortal,
    DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Loader, Archive, Pencil } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "../ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Timestamp } from "firebase/firestore"
import toast from "react-hot-toast"
import { useState, Suspense, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { OrganizationDetails } from "./components/OrganizationDetails"
import { FinancialDetails } from "./components/FinancialDetails"
import { VotingSection } from "./components/VotingSection"
import { CommentsSection } from "./components/CommentsSection"
import NotifyApplicantByEmailCheckbox from "./components/NotifyApplicantByEmailCheckbox"
import { formatGBP } from "./utils"

interface ViewApplicationModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    applicationId: string
}

export default function ViewApplicationModal(props: ViewApplicationModalProps) {
    const [view, setView] = useState<"accept" | "decline" | null>(null)
    const [grantAmount, setGrantAmount] = useState("")
    const [acceptReason, setAcceptReason] = useState("")
    const [declineReason, setDeclineReason] = useState("")
    const [notifyByEmail, setNotifyByEmail] = useState(false)
    const [detailsPane, setDetailsPane] = useState("details")

    const queryClient = useQueryClient()
    const navFunc = useNavigate()
    const user = useGetUserById(navFunc)
    const application = useFetchApplication(props.applicationId)
    const canFetchTrustees =
        user.data?.role === "admin" || user.data?.role === "trustee"
    const { data: trustees, isPending: isTrusteesLoading } =
        useFetchTrustees(canFetchTrustees)
    const { mutate: addComment, isPending: isAddCommentPending } =
        useAddCommentToApplication(props.applicationId)
    const { mutate: addVote, isPending: isAddVotePending } =
        useAddVoteToApplication(props.applicationId)
    const { mutate: updateCategory, isPending: isUpdateCategoryPending } =
        useUpdateApplicationCategory(props.applicationId)
    const { mutate: updateStatus, isPending: isUpdateStatusPending } =
        useUpdateApplicationStatus(props.applicationId)
    const { mutate: archiveApplication } = useArchiveApplications()
    const { mutate: unarchiveApplication } = useUnarchiveApplications()

    function handleCastVote(vote: string) {
        const voteInt = parseInt(vote)
        if (!user.data?.userId) return
        if (!props.applicationId) return

        addVote({
            applicationId: props.applicationId,
            trusteeId: user.data.userId,
            voteId: uniqid(),
            score: voteInt,
            voteDate: Timestamp.now(),
        })
    }

    function handleAddComment(comment: string) {
        addComment({
            date: Timestamp.now(),
            applicationId: props.applicationId,
            type: "admin",
            body: comment,
            userId: user.data?.userId || null,
            userName:
                user.data?.firstName && user.data?.lastName
                    ? `${user.data?.firstName} ${user.data?.lastName}`
                    : null,
        })
    }

    const handleArchive = () => {
        if (application.data?.application.isArchived) {
            unarchiveApplication([props.applicationId])
        } else {
            archiveApplication([props.applicationId])
        }
    }

    useEffect(() => {
        // We'll automatically notify by email if the application has not been decided yet
        setNotifyByEmail(
            !application.data?.application.dateDecide ? true : false
        )
        switch (view) {
            case "accept":
                setAcceptReason(
                    application.data?.application.acceptReason || ""
                )
                setGrantAmount(
                    application.data?.application.grantAmount
                        ? application.data?.application.grantAmount.toString()
                        : ""
                )
                break
            case "decline":
                setDeclineReason(
                    application.data?.application.declineReason || ""
                )
                break
            default:
                setAcceptReason("")
                setGrantAmount("")
                setDeclineReason("")
                break
        }
    }, [application.data?.application, view])

    return (
        <Dialog
            open={props.open}
            onOpenChange={(change) => {
                if (!change) {
                    queryClient.invalidateQueries({
                        predicate: (query) => {
                            return query.queryKey[0] === "applications"
                        },
                    })
                    props.onOpenChange(false)
                }
            }}
        >
            <DialogTitle className="sr-only">
                {application.data?.application.orgName ?? "View Application"}
            </DialogTitle>
            <DialogDescription className="sr-only">
                View and manage the application
            </DialogDescription>
            <DialogPortal>
                <DialogContent className="max-w-screen top-0 max-h-screen overflow-y-auto p-4 md:p-6 md:max-h-[90dvh] left-0 md:top-1/2 md:left-1/2 translate-x-0 translate-y-0 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-[1280px] md:w-[90vw]">
                    {application.isLoading ? (
                        <div className="flex justify-center items-center gap-2 h-[50vh]">
                            <p className="text-lg font-bold">
                                Fetching Application Data
                            </p>
                            <Loader
                                className="animate-spin text-app-default"
                                size={28}
                            />
                        </div>
                    ) : !application.data?.application ? (
                        <div className="flex justify-center items-center gap-2 h-[50vh]">
                            <p className="text-lg font-bold">
                                No application data found
                            </p>
                        </div>
                    ) : (
                        <>
                            <DialogHeader>
                                <div className="flex gap-2 flex-col md:flex-row md:justify-between md:items-center mt-2 md:mt-4">
                                    <DialogTitle className="text-xl md:text-2xl mb-2">
                                        <a
                                            href={
                                                application.data.application
                                                    .orgURL || "#"
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-app-defaultLight capitalize underline"
                                        >
                                            {
                                                application.data.application
                                                    .orgName
                                            }
                                        </a>
                                    </DialogTitle>
                                    {user.data?.role === "admin" && (
                                        <div className="flex flex-wrap justify-center md:justify-end items-center gap-2 md:gap-4">
                                            {application.data.application
                                                .reviewStatus !== "accepted" &&
                                                application.data.application
                                                    .reviewStatus !==
                                                    "declined" &&
                                                !application.data.application
                                                    .isArchived && (
                                                    <>
                                                        <Button
                                                            variant="accept"
                                                            onClick={() =>
                                                                setView(
                                                                    "accept"
                                                                )
                                                            }
                                                            disabled={
                                                                isUpdateStatusPending
                                                            }
                                                            className="w-full md:w-auto"
                                                        >
                                                            Accept
                                                        </Button>
                                                        <Button
                                                            onClick={() =>
                                                                setView(
                                                                    "decline"
                                                                )
                                                            }
                                                            variant="decline"
                                                            disabled={
                                                                isUpdateStatusPending
                                                            }
                                                            className="w-full md:w-auto"
                                                        >
                                                            Decline
                                                        </Button>
                                                    </>
                                                )}
                                            {(application.data.application
                                                .reviewStatus === "accepted" ||
                                                application.data.application
                                                    .reviewStatus ===
                                                    "declined") && (
                                                <div className="px-4 py-2 rounded-md bg-gray-100 flex items-center gap-2">
                                                    <p
                                                        className={`text-sm font-medium capitalize ${
                                                            application.data
                                                                .application
                                                                .reviewStatus ===
                                                            "accepted"
                                                                ? "text-emerald-600"
                                                                : "text-rose-600"
                                                        }`}
                                                    >
                                                        Status:{" "}
                                                        {
                                                            application.data
                                                                .application
                                                                .reviewStatus
                                                        }
                                                    </p>
                                                    {application.data
                                                        .application
                                                        .reviewStatus ===
                                                        "accepted" && (
                                                        <p className="text-sm">
                                                            Grant Amount:{" "}
                                                            {formatGBP(
                                                                application.data
                                                                    .application
                                                                    .grantAmount ||
                                                                    0
                                                            )}
                                                        </p>
                                                    )}
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => {
                                                            let view:
                                                                | "accept"
                                                                | "decline"
                                                                | null
                                                            switch (
                                                                application.data
                                                                    .application
                                                                    .reviewStatus
                                                            ) {
                                                                case "accepted":
                                                                    view =
                                                                        "accept" as const
                                                                    break
                                                                case "declined":
                                                                    view =
                                                                        "decline"
                                                                    break
                                                                default:
                                                                    view = null
                                                            }
                                                            setView(view)
                                                        }}
                                                    >
                                                        <Pencil
                                                            color={"gray"}
                                                            width={16}
                                                            height={16}
                                                        />
                                                    </Button>
                                                </div>
                                            )}
                                            <Button
                                                variant="outline"
                                                onClick={handleArchive}
                                            >
                                                <Archive className="mr-2 h-4 w-4" />
                                                {application.data.application
                                                    .isArchived
                                                    ? "Unarchive"
                                                    : "Archive"}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </DialogHeader>

                            <div className="flex flex-col gap-3 md:gap-4">
                                {view === "accept" && (
                                    <section className="bg-gray-100 border border-input flex flex-col gap-2 rounded-md p-3 md:p-4">
                                        <div>
                                            <h2 className="text-lg font-semibold">
                                                Grant Amount
                                            </h2>
                                            <p className="text-sm text-gray-500">
                                                Enter the amount to be granted
                                                to{" "}
                                                {
                                                    application.data.application
                                                        .orgName
                                                }
                                            </p>
                                        </div>
                                        <section className="flex flex-col md:flex-row gap-2">
                                            <div className="flex items-center gap-2 w-full md:w-auto">
                                                <span className="text-lg">
                                                    £
                                                </span>
                                                <Input
                                                    type="number"
                                                    placeholder="Enter grant amount"
                                                    value={grantAmount}
                                                    onChange={(e) =>
                                                        setGrantAmount(
                                                            e.target.value
                                                        )
                                                    }
                                                    className="w-full md:w-[200px]"
                                                />
                                            </div>
                                        </section>
                                        <section className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="text"
                                                    placeholder="(Optional) Enter a custom acceptance message to the applicant"
                                                    value={acceptReason}
                                                    onChange={(e) =>
                                                        setAcceptReason(
                                                            e.target.value
                                                        )
                                                    }
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="flex flex-col md:flex-row gap-2">
                                                <Button
                                                    className="w-full md:w-[100px]"
                                                    variant="default"
                                                    onClick={() => {
                                                        if (!grantAmount) {
                                                            toast.error(
                                                                "Please enter a grant amount"
                                                            )
                                                            return
                                                        }
                                                        updateStatus({
                                                            reviewStatus:
                                                                "accepted",
                                                            grantAmount:
                                                                parseFloat(
                                                                    grantAmount
                                                                ),
                                                            acceptReason,
                                                            sendEmailNotification:
                                                                notifyByEmail,
                                                        })
                                                        setView(null)
                                                        toast.success(
                                                            "Application accepted successfully"
                                                        )
                                                    }}
                                                    disabled={
                                                        isUpdateStatusPending
                                                    }
                                                >
                                                    {isUpdateStatusPending ? (
                                                        <Loader className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        "Confirm"
                                                    )}
                                                </Button>
                                                <Button
                                                    className="w-full md:w-[100px]"
                                                    variant="outline"
                                                    onClick={() =>
                                                        setView(null)
                                                    }
                                                    disabled={
                                                        isUpdateStatusPending
                                                    }
                                                >
                                                    Cancel
                                                </Button>
                                                {application.data.application
                                                    .dateDecide && (
                                                    <Button
                                                        variant="decline"
                                                        onClick={() => {
                                                            setView("decline")
                                                        }}
                                                    >
                                                        Change to Decline
                                                    </Button>
                                                )}
                                                <NotifyApplicantByEmailCheckbox
                                                    notifyByEmail={
                                                        notifyByEmail
                                                    }
                                                    setNotifyByEmail={
                                                        setNotifyByEmail
                                                    }
                                                />
                                            </div>
                                        </section>
                                    </section>
                                )}

                                {view === "decline" && (
                                    <section className="bg-gray-100 border border-input flex flex-col gap-2 rounded-md p-3 md:p-4">
                                        <div>
                                            <h2 className="text-lg font-semibold">
                                                Decline Reason
                                            </h2>
                                        </div>
                                        <section className="flex flex-col gap-2">
                                            <Input
                                                placeholder="Reason for decline"
                                                value={declineReason}
                                                onChange={(e) =>
                                                    setDeclineReason(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <div className="flex flex-col md:flex-row gap-2">
                                                <Button
                                                    className="w-full md:w-[100px]"
                                                    variant="destructive"
                                                    onClick={() => {
                                                        if (!declineReason) {
                                                            toast.error(
                                                                "Please enter a reason for declining"
                                                            )
                                                            return
                                                        }
                                                        updateStatus({
                                                            reviewStatus:
                                                                "declined",
                                                            declineReason,

                                                            sendEmailNotification:
                                                                notifyByEmail,
                                                        })
                                                        setView(null)
                                                        toast.success(
                                                            "Application declined successfully"
                                                        )
                                                    }}
                                                    disabled={
                                                        isUpdateStatusPending
                                                    }
                                                >
                                                    {isUpdateStatusPending ? (
                                                        <Loader className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        "Confirm"
                                                    )}
                                                </Button>
                                                <Button
                                                    className="w-full md:w-[100px]"
                                                    variant="outline"
                                                    onClick={() =>
                                                        setView(null)
                                                    }
                                                    disabled={
                                                        isUpdateStatusPending
                                                    }
                                                >
                                                    Cancel
                                                </Button>
                                                {application.data.application
                                                    .dateDecide && (
                                                    <Button
                                                        variant="accept"
                                                        onClick={() => {
                                                            setView("accept")
                                                        }}
                                                    >
                                                        Change to Accept
                                                    </Button>
                                                )}
                                                <NotifyApplicantByEmailCheckbox
                                                    notifyByEmail={
                                                        notifyByEmail
                                                    }
                                                    setNotifyByEmail={
                                                        setNotifyByEmail
                                                    }
                                                />
                                            </div>
                                        </section>
                                    </section>
                                )}

                                <Suspense
                                    fallback={
                                        <div className="bg-gray-100 border border-input rounded-md p-4 h-32 animate-pulse" />
                                    }
                                >
                                    <OrganizationDetails
                                        application={
                                            application.data.application
                                        }
                                        userEmail={
                                            application.data.user?.email ?? ""
                                        }
                                        userName={`${application.data.user?.firstName ?? ""} ${application.data.user?.lastName ?? ""}`}
                                    />
                                </Suspense>

                                <Suspense
                                    fallback={
                                        <div className="bg-gray-100 border border-input rounded-md p-4 h-32 animate-pulse" />
                                    }
                                >
                                    <FinancialDetails
                                        application={
                                            application.data.application
                                        }
                                    />
                                </Suspense>

                                <Suspense
                                    fallback={
                                        <div className="bg-gray-100 border border-input rounded-md p-4 h-32 animate-pulse" />
                                    }
                                >
                                    <section className="bg-gray-100 border border-input flex flex-col gap-2 rounded-md p-3 md:p-4">
                                        <div>
                                            <h2 className="text-lg font-semibold">
                                                Application Category and Voting
                                                Status
                                            </h2>
                                        </div>

                                        <div>
                                            <h3 className="font-bold">
                                                Category
                                            </h3>
                                            {user.data?.role === "admin" ? (
                                                <div className="flex items-center gap-2">
                                                    <Select
                                                        defaultValue={
                                                            application.data
                                                                .application
                                                                .category || ""
                                                        }
                                                        onValueChange={
                                                            updateCategory
                                                        }
                                                        disabled={
                                                            isUpdateCategoryPending
                                                        }
                                                    >
                                                        <SelectTrigger className="w-[180px]">
                                                            <SelectValue placeholder="Select category" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="Category A">
                                                                Category A
                                                            </SelectItem>
                                                            <SelectItem value="Category B">
                                                                Category B
                                                            </SelectItem>
                                                            <SelectItem value="Category C">
                                                                Category C
                                                            </SelectItem>
                                                            <SelectItem value="Category D">
                                                                Category D
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    {isUpdateCategoryPending && (
                                                        <Loader className="h-4 w-4 animate-spin" />
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-sm">
                                                    {application.data
                                                        .application.category ||
                                                        "Not specified"}
                                                </p>
                                            )}
                                        </div>

                                        <VotingSection
                                            application={
                                                application.data.application
                                            }
                                            applicationId={props.applicationId}
                                            userRole={user.data?.role || ""}
                                            userId={user.data?.userId}
                                            trustees={trustees || []}
                                            isTrusteesLoading={
                                                isTrusteesLoading
                                            }
                                            onVote={handleCastVote}
                                            isAddVotePending={isAddVotePending}
                                        />
                                    </section>
                                </Suspense>

                                <Suspense
                                    fallback={
                                        <div className="bg-gray-100 border border-input rounded-md p-4 h-32 animate-pulse" />
                                    }
                                >
                                    <section>
                                        <h2 className="text-lg font-semibold mb-2">
                                            More Information
                                        </h2>
                                        <Select
                                            value={detailsPane}
                                            onValueChange={setDetailsPane}
                                        >
                                            <SelectTrigger className="flex md:hidden mb-4">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="details">
                                                    Organisation Aims
                                                </SelectItem>
                                                <SelectItem value="beneficiaries">
                                                    Organisation Beneficiaries
                                                </SelectItem>
                                                <SelectItem value="documents">
                                                    Supporting Documents
                                                </SelectItem>
                                                <SelectItem value="comments">
                                                    Comments
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Tabs
                                            value={detailsPane}
                                            onValueChange={setDetailsPane}
                                            defaultValue="details"
                                            className="w-full"
                                        >
                                            <TabsList className="hidden md:flex justify-start w-full md:w-auto overflow-x-auto">
                                                <TabsTrigger value="details">
                                                    Organisation Aims
                                                </TabsTrigger>
                                                <TabsTrigger value="beneficiaries">
                                                    Organisation Beneficiaries
                                                </TabsTrigger>
                                                <TabsTrigger value="documents">
                                                    Supporting Documents
                                                </TabsTrigger>
                                                <TabsTrigger value="comments">
                                                    Comments
                                                </TabsTrigger>
                                            </TabsList>
                                            <TabsContent value="details">
                                                <section className="bg-gray-100 border border-input flex flex-col gap-2 rounded-md p-3 md:p-4 w-full">
                                                    <h3 className="font-bold">
                                                        Aims of Organisation
                                                    </h3>
                                                    <p>
                                                        {
                                                            application.data
                                                                .application
                                                                .orgAims
                                                        }
                                                    </p>
                                                </section>
                                            </TabsContent>
                                            <TabsContent value="beneficiaries">
                                                <section className="bg-gray-100 border border-input flex flex-col gap-4 rounded-md p-3 md:p-4 w-full">
                                                    <section className="flex flex-col gap-1">
                                                        <h3 className="font-bold">
                                                            Age Group
                                                        </h3>
                                                        <p>
                                                            {application.data
                                                                .application
                                                                .ageGroup ||
                                                                "Not specified"}
                                                        </p>
                                                    </section>
                                                    <section className="flex flex-col gap-1">
                                                        <h3 className="font-bold">
                                                            My organisation
                                                            works with Veterans
                                                        </h3>
                                                        <p>
                                                            {application.data
                                                                .application
                                                                .veteransCheck ||
                                                                "Not specified"}
                                                        </p>
                                                    </section>
                                                    <section className="flex flex-col gap-1">
                                                        <h3 className="font-bold">
                                                            Sector
                                                        </h3>
                                                        <p>
                                                            {application.data
                                                                .application
                                                                .sector ||
                                                                "Not specified"}
                                                        </p>
                                                    </section>
                                                    <section className="flex flex-col gap-1">
                                                        <h3 className="font-bold">
                                                            Sub Sector
                                                        </h3>
                                                        <p>
                                                            {application.data
                                                                .application
                                                                .subsector ||
                                                                "Not specified"}
                                                        </p>
                                                    </section>
                                                </section>
                                            </TabsContent>
                                            <TabsContent value="documents">
                                                <section className="bg-gray-100 border border-input flex flex-col gap-4 rounded-md p-3 md:p-4 w-full">
                                                    <section className="flex flex-col gap-2">
                                                        <h3 className="font-bold">
                                                            Supporting Documents
                                                        </h3>
                                                        {application.data
                                                            .application
                                                            .applicantFiles
                                                            ?.length ? (
                                                            application.data.application.applicantFiles.map(
                                                                (
                                                                    doc,
                                                                    index
                                                                ) => (
                                                                    <a
                                                                        href={
                                                                            doc.path
                                                                        }
                                                                        download={
                                                                            doc.path
                                                                        }
                                                                        target="_blank"
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="text-app-defaultLight underline cursor-pointer"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {
                                                                            doc.name
                                                                        }
                                                                    </a>
                                                                )
                                                            )
                                                        ) : (
                                                            <p>
                                                                No documents
                                                                available
                                                            </p>
                                                        )}
                                                    </section>
                                                </section>
                                            </TabsContent>
                                            <TabsContent value="comments">
                                                <CommentsSection
                                                    comments={
                                                        application.data
                                                            .application
                                                            .comments || []
                                                    }
                                                    onAddComment={
                                                        handleAddComment
                                                    }
                                                    isAddCommentPending={
                                                        isAddCommentPending
                                                    }
                                                    userRole={
                                                        user.data?.role || ""
                                                    }
                                                />
                                            </TabsContent>
                                        </Tabs>
                                    </section>
                                </Suspense>
                            </div>
                        </>
                    )}
                </DialogContent>
            </DialogPortal>
        </Dialog>
    )
}
